// Asset-allocation donut chart.
//
// Renders the seven-bucket allocation returned by
// ``portfolio.allocation`` from /calculate-full-profile as a single
// SVG donut + a colour-coded legend. Hand-rolled (no recharts) to
// match the rest of the dependency-free design system.
//
// Geometry: each slice is an SVG <path> built from two arcs (outer
// ring + inner ring), which gives the donut a clean punched-out
// centre regardless of slice count. Sub-percent slices are drawn
// faithfully but excluded from the legend so the side panel stays
// readable.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

export interface AllocationDonutProps {
    /** { "Equity": 60.5, "Bonds": 30.0, ... } -- percent values that
     *  sum to ~100. */
    allocation: Record<string, number>;
    /** SVG outer width/height. Defaults to 240px. */
    size?: number;
    /** Optional resolver mapping a legend label to an in-app route. When
     *  it returns a truthy path, that legend row renders as a clickable
     *  <Link> to the corresponding page. Omitting it (the default) keeps
     *  the legend static, so existing call-sites are unaffected. */
    linkFor?: (label: string) => string | undefined;
}

// Slice colours. Order matters -- the slices are drawn in the same
// order as the keys arrive, so the colour cycles map onto the asset
// classes the backend returns (Equity / Bonds / Cash / Alternatives /
// REITs / Commodities / Crypto today).
const PALETTE = [
    '#10b981', // emerald-500   — primary accent
    '#3b82f6', // blue-500
    '#facc15', // yellow-400
    '#6366f1', // indigo-500
    '#f97316', // orange-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
    '#84cc16', // lime-500
];

/** Polar -> cartesian. Same convention as RiskRadarChart: 12 o'clock
 *  origin, clockwise. Lets us share mental model across charts. */
function polar(cx: number, cy: number, r: number, angleRad: number) {
    return {
        x: cx + r * Math.sin(angleRad),
        y: cy - r * Math.cos(angleRad),
    };
}

/** Build the SVG ``d`` for a single donut slice given start + end
 *  angles in radians and the inner / outer radii. */
function donutSlicePath(
    cx: number,
    cy: number,
    rOuter: number,
    rInner: number,
    startAngle: number,
    endAngle: number,
): string {
    const o1 = polar(cx, cy, rOuter, startAngle);
    const o2 = polar(cx, cy, rOuter, endAngle);
    const i1 = polar(cx, cy, rInner, endAngle);
    const i2 = polar(cx, cy, rInner, startAngle);
    // SVG arcs need a "large arc" flag when the swept angle exceeds π.
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return [
        `M ${o1.x} ${o1.y}`,
        `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${o2.x} ${o2.y}`,
        `L ${i1.x} ${i1.y}`,
        `A ${rInner} ${rInner} 0 ${largeArc} 0 ${i2.x} ${i2.y}`,
        'Z',
    ].join(' ');
}

interface PreparedSlice {
    label: string;
    value: number;     // raw percent (e.g. 30.5)
    fraction: number;  // value / total -- used for arc geometry
    color: string;
    startAngle: number;
    endAngle: number;
}

export const AllocationDonut = ({
    allocation,
    size = 240,
    linkFor,
}: AllocationDonutProps) => {
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);

    // Pre-compute geometry once per allocation change. Filtering out
    // 0% slices keeps the SVG (and legend) tidy on aggressive
    // portfolios that allocate nothing to e.g. Cash.
    const slices = useMemo<PreparedSlice[]>(() => {
        const entries = Object.entries(allocation).filter(([, v]) => v > 0);
        const total = entries.reduce((sum, [, v]) => sum + v, 0) || 1;
        let cursor = 0;
        return entries.map(([label, value], idx) => {
            const fraction = value / total;
            const start = cursor * 2 * Math.PI;
            cursor += fraction;
            const end = cursor * 2 * Math.PI;
            return {
                label,
                value,
                fraction,
                color: PALETTE[idx % PALETTE.length],
                startAngle: start,
                endAngle: end,
            };
        });
    }, [allocation]);

    if (slices.length === 0) {
        return <p className="muted text-sm">No allocation to display.</p>;
    }

    const cx = size / 2;
    const cy = size / 2;
    const rOuter = size / 2 - 4;
    const rInner = rOuter * 0.62;

    const hovered = hoverIdx !== null ? slices[hoverIdx] : null;

    return (
        <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative" style={{ width: size, height: size }}>
                <svg
                    viewBox={`0 0 ${size} ${size}`}
                    width={size}
                    height={size}
                    role="img"
                    aria-label="Asset allocation donut chart"
                >
                    {slices.map((slice, idx) => {
                        // Edge-case: a single 100% slice would degenerate the
                        // arc into start==end. Render a full ring instead.
                        const path =
                            slice.fraction >= 0.999
                                ? `M ${cx + rOuter} ${cy} A ${rOuter} ${rOuter} 0 1 1 ${
                                      cx - rOuter
                                  } ${cy} A ${rOuter} ${rOuter} 0 1 1 ${cx + rOuter} ${cy} ` +
                                  `M ${cx + rInner} ${cy} A ${rInner} ${rInner} 0 1 0 ${
                                      cx - rInner
                                  } ${cy} A ${rInner} ${rInner} 0 1 0 ${cx + rInner} ${cy} Z`
                                : donutSlicePath(
                                      cx,
                                      cy,
                                      rOuter,
                                      rInner,
                                      slice.startAngle,
                                      slice.endAngle,
                                  );
                        const isActive = hoverIdx === idx;
                        return (
                            <path
                                key={slice.label}
                                d={path}
                                fill={slice.color}
                                fillRule="evenodd"
                                className="transition-opacity"
                                opacity={hoverIdx === null || isActive ? 1 : 0.45}
                                onMouseEnter={() => setHoverIdx(idx)}
                                onMouseLeave={() => setHoverIdx(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <title>
                                    {slice.label}: {slice.value.toFixed(1)}%
                                </title>
                            </path>
                        );
                    })}
                </svg>

                {/* Centre label -- hovered slice if any, otherwise a
                    summary "Allocation / 100%" pair. */}
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                    {hovered ? (
                        <>
                            <span className="text-xs uppercase tracking-wide muted">
                                {hovered.label}
                            </span>
                            <span className="text-2xl font-black tabular-nums text-slate-900 dark:text-white">
                                {hovered.value.toFixed(1)}%
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="text-xs uppercase tracking-wide muted">
                                Allocation
                            </span>
                            <span className="text-2xl font-black tabular-nums text-slate-900 dark:text-white">
                                100%
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Legend. ``min-w-0`` so long asset names truncate cleanly
                inside flex containers. */}
            <ul className="flex-1 min-w-0 w-full space-y-2">
                {slices.map((slice, idx) => {
                    const href = linkFor?.(slice.label);
                    const rowClass = [
                        'flex items-center gap-3 rounded-lg px-2 py-1 transition-colors',
                        hoverIdx === idx
                            ? 'bg-slate-100 dark:bg-slate-800'
                            : '',
                    ].join(' ');
                    const swatch = (
                        <span
                            aria-hidden
                            className="h-3 w-3 shrink-0 rounded-sm"
                            style={{ backgroundColor: slice.color }}
                        />
                    );
                    const value = (
                        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                            {slice.value.toFixed(1)}%
                        </span>
                    );
                    return (
                        <li
                            key={slice.label}
                            onMouseEnter={() => setHoverIdx(idx)}
                            onMouseLeave={() => setHoverIdx(null)}
                        >
                            {href ? (
                                <Link
                                    to={href}
                                    title={`View ${slice.label} ranking`}
                                    className={`${rowClass} group cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800`}
                                >
                                    {swatch}
                                    <span className="flex-1 truncate text-sm font-medium text-slate-700 group-hover:text-emerald-600 dark:text-slate-200 dark:group-hover:text-emerald-400 transition-colors">
                                        {slice.label}
                                    </span>
                                    <svg
                                        aria-hidden
                                        className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-emerald-500 transition-colors"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="9 18 15 12 9 6" />
                                    </svg>
                                    {value}
                                </Link>
                            ) : (
                                <div className={rowClass}>
                                    {swatch}
                                    <span className="flex-1 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                                        {slice.label}
                                    </span>
                                    {value}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default AllocationDonut;
